import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      app: {
        title: 'Hvozdici Management',
        subtitle: 'Manage members and game statistics',
      },
      nav: {
        section: { ops: 'Operations', settings: 'Settings' },
        dashboard: 'Overview',
        games: 'Games',
        gameDetail: 'Game detail',
        members: 'Members',
        groups: 'Groups',
        stats: 'Statistics',
        qrCodes: 'QR codes',
        devices: 'Devices',
        epochs: 'Epochs',
        gameTypes: 'Game modes',
      },
      common: {
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        cancel: 'Cancel',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        creating: 'Creating...',
        close: 'Close',
        confirm: 'Confirm',
        confirmDelete: 'Click again to confirm',
        search: 'Search...',
        created: 'Created',
      },
      members: {
        title: 'Members',
        addNew: 'Add New Member',
        editMember: 'Edit Member',
        deleteMember: 'Delete Member',
        noMembers: 'No members found',
        selectGroup: 'Select a group...',
        fields: {
          number: 'Number',
          name: 'Name',
          group: 'Group',
          createdAt: 'Created At',
          updatedAt: 'Updated At',
        },
        validation: {
          numberRequired: 'Number is required',
          numberPositive: 'Number must be positive',
          nameRequired: 'Name is required',
          groupRequired: 'Group is required',
        },
        messages: {
          createSuccess: 'Member created successfully',
          updateSuccess: 'Member updated successfully',
          deleteSuccess: 'Member deleted successfully',
          deleteConfirm: 'Are you sure you want to delete this member?',
          numberExists: 'Member number already exists',
        },
      },
      games: {
        loading: 'Loading statistics...',
        loadingScores: 'Loading scores...',
        loadError: 'Failed to load statistics',
        totalGames: 'Total Games',
        activeGames: 'Active',
        totalDeaths: 'Total Deaths',
        totalTime: 'Total Death Time',
        overallLeaderboard: 'Overall Leaderboard',
        gamesHistory: 'Games History',
        newGame: 'New Game',
        noGames: 'No games yet',
        active: 'Active',
        ongoing: 'Ongoing',
        member: 'Member',
        deathTime: 'Death Time',
        deaths: 'Deaths',
        gamesPlayed: 'Games',
        gameName: 'Game Name',
        gameNamePlaceholder: 'e.g., Saturday Mrtvoliště',
        nameRequired: 'Game name is required',
        createError: 'Failed to create game',
        started: 'Started',
        ended: 'Ended',
        duration: 'Duration',
        results: 'Results',
        noScores: 'No scores yet',
        internalMrtvoliste: 'Mrtvoliště',
        endGame: 'End Game',
        ending: 'Ending...',
        confirmEnd: 'Are you sure you want to end this game?',
        endError: 'Failed to end game',
        noHostinaResults: 'No Hostina results yet',
        recipe: 'recipe',
        recipes: 'recipes',
        gameHistory: 'Game History',
        allCompletedGames: 'All completed games',
        viewResults: 'View Results',
        backToOverview: 'Back to Overview',
        overallStatistics: 'Overall Statistics',
        comprehensiveOverview: 'Comprehensive overview of all games',
        basicOverview: 'Basic Overview',
        completed: 'Completed',
        totalDeathTime: 'Total Death Time',
        avgDeathsPerGame: 'Avg. Deaths/Game',
        gameDuration: 'Game Duration',
        avgGameDuration: 'Average Game Duration',
        totalGameTime: 'Total Game Time',
        mostDeadPlayers: 'Most Dead Players',
        noData: 'No data available',
        mostValuablePlayers: 'Most Valuable Players',
        totalVotes: 'Total Votes',
        gamesVoted: 'Games Voted',
      },
      groups: {
        title: 'Groups',
        subtitle: 'Manage member groups and teams',
        addNew: 'Add New Group',
        create: 'Create Group',
        edit: 'Edit Group',
        members: 'members',
        empty: {
          title: 'No groups yet',
          description: 'Create your first group to organize members into teams.',
          action: 'Create First Group',
        },
        form: {
          name: 'Group Name',
          namePlaceholder: 'Enter group name...',
        },
        delete: {
          title: 'Delete Group?',
          message: 'Are you sure you want to delete the group "{{name}}"? This action cannot be undone.',
          warning: 'This group has {{count}} member(s). They will lose their group assignment.',
        },
        error: {
          loadFailed: 'Failed to load groups',
          nameRequired: 'Group name is required',
          saveFailed: 'Failed to save group',
          deleteFailed: 'Failed to delete group',
        },
      },
      errors: {
        fetchFailed: 'Failed to fetch members',
        createFailed: 'Failed to create member',
        updateFailed: 'Failed to update member',
        deleteFailed: 'Failed to delete member',
        networkError: 'Network error. Please check your connection.',
      },
      clientSelector: {
        label: 'Client',
        allClients: 'All Clients (Admin View)',
        selected: 'Selected',
        loading: 'Loading clients...',
        loadError: 'Failed to load clients',
      },
      privacy: {
        title: 'Privacy Policy',
        lastUpdated: 'Last Updated',
        dataCollection: {
          title: 'What Data We Collect',
          intro: 'The Hvozdici application collects the following data to provide its functionality:',
          deviceId: 'Device identifier for pairing',
          qrCodes: 'QR codes of game participants',
          gameStats: 'Game statistics (scores, times)',
          notifications: 'Push notification tokens',
        },
        dataUse: {
          title: 'How We Use Your Data',
          description: 'We use the collected data exclusively to provide the functionality of the application and game modes.',
        },
        dataSharing: {
          title: 'Data Sharing',
          description: 'We do not share your data with third parties.',
        },
        dataRetention: {
          title: 'Data Retention',
          description: 'Data is stored on our server for the duration of using the application.',
        },
        security: {
          title: 'Data Security',
          description: 'We implement appropriate technical and organizational measures to protect your data.',
        },
        userRights: {
          title: 'Your Rights',
          intro: 'You have the following rights regarding your data:',
          access: 'Right to access your data',
          deletion: 'Right to request deletion of your data',
          correction: 'Right to correction of inaccurate data',
        },
        contact: {
          title: 'Contact',
          description: 'If you have any questions regarding the protection of your personal data, please contact us at:',
        },
        footer: 'This privacy policy applies to the Hvozdici mobile application and associated web services.',
      },
      admin: {
        title: 'Administration',
        subtitle: 'Manage system data and perform bulk operations',
        warning: {
          title: 'Warning: Destructive Operations',
          message: 'These operations cannot be undone. Make sure you have a backup before proceeding.',
        },
        deleteStats: {
          title: 'Delete All Statistics',
          description: 'Remove all game records and scores from the database.',
          item1: 'All games will be deleted',
          item2: 'All death records will be deleted',
          item3: 'Overall statistics will be reset',
          button: 'Delete All Statistics',
        },
        deleteHistory: {
          title: 'Delete Games History',
          description: 'Remove only ended games and their scores. Active games will be preserved.',
          item1: 'Only ended games will be deleted',
          item2: 'Active games will be kept',
          item3: 'Overall statistics will be updated',
          button: 'Delete Games History',
        },
        deleteGroups: {
          title: 'Delete All Groups',
          description: 'Remove all groups from the database.',
          item1: 'All groups will be deleted',
          item2: 'Members will lose their group assignments',
          button: 'Delete All Groups',
        },
        deleteMembers: {
          title: 'Delete All Members',
          description: 'Remove all members from the database.',
          item1: 'All members will be deleted',
          item2: 'All associated statistics will be orphaned',
          button: 'Delete All Members',
        },
        confirm: {
          statsTitle: 'Delete All Statistics?',
          statsMessage: 'This will permanently delete all games and death records. This action cannot be undone. Are you sure you want to continue?',
          historyTitle: 'Delete Games History?',
          historyMessage: 'This will permanently delete all ended games and their death records. Active games will be preserved. This action cannot be undone. Are you sure you want to continue?',
          groupsTitle: 'Delete All Groups?',
          groupsMessage: 'This will permanently delete all groups. Members will lose their group assignments. This action cannot be undone. Are you sure you want to continue?',
          membersTitle: 'Delete All Members?',
          membersMessage: 'This will permanently delete all members from the database. This action cannot be undone. Are you sure you want to continue?',
        },
        messages: {
          statsDeleted: 'All statistics have been deleted successfully.',
          statsDeleteError: 'Failed to delete statistics. Please try again.',
          historyDeleted: 'Games history has been deleted successfully.',
          historyDeleteError: 'Failed to delete games history. Please try again.',
          groupsDeleted: 'All groups have been deleted successfully.',
          groupsDeleteError: 'Failed to delete groups. Please try again.',
          membersDeleted: 'All members have been deleted successfully.',
          membersDeleteError: 'Failed to delete members. Please try again.',
        },
        devices: {
          title: 'Device Management',
          description: 'Manage paired mobile devices for this client',
          pairButton: 'Pair Device',
          pairFirstDevice: 'Pair First Device',
          activeTab: 'Active',
          unpairedTab: 'Unpaired',
          noClient: 'No client selected',
          selectClient: 'Please select a client from the dropdown above to manage devices.',
          noClientSelected: 'No client selected',
          noDevices: 'No paired devices',
          noDevicesDescription: 'This client has no paired mobile devices yet.',
          noUnpairedDevices: 'No unpaired devices',
          noUnpairedDescription: 'No devices have been unpaired.',
          loadError: 'Failed to load devices',
          paired: 'Paired',
          unpaired: 'Unpaired',
          platform: 'Platform',
          appVersion: 'App Version',
          confirmUnpair: 'Are you sure you want to unpair this device? The mobile app will be logged out.',
          unpairButton: 'Unpair Device',
          unpairing: 'Unpairing...',
          unpairError: 'Failed to unpair device',
          admin: 'Admin',
          grantAdmin: 'Grant admin rights',
          removeAdmin: 'Remove admin rights',
          adminToggleError: 'Failed to change admin rights',
        },
      },
    },
  },
  cs: {
    translation: {
      app: {
        title: 'Správa Hvozdici',
        subtitle: 'Spravujte členy a herní statistiky',
      },
      nav: {
        section: { ops: 'Provoz', settings: 'Nastavení' },
        dashboard: 'Přehled',
        games: 'Hry',
        gameDetail: 'Detail hry',
        members: 'Členové',
        groups: 'Skupiny',
        stats: 'Statistiky',
        qrCodes: 'QR kódy',
        devices: 'Zařízení',
        epochs: 'Epochy',
        gameTypes: 'Herní módy',
      },
      common: {
        loading: 'Načítání...',
        error: 'Chyba',
        success: 'Úspěch',
        cancel: 'Zrušit',
        save: 'Uložit',
        delete: 'Smazat',
        edit: 'Upravit',
        create: 'Vytvořit',
        creating: 'Vytvářím...',
        close: 'Zavřít',
        confirm: 'Potvrdit',
        confirmDelete: 'Klikněte znovu pro potvrzení',
        search: 'Hledat...',
        created: 'Vytvořeno',
      },
      members: {
        title: 'Členové',
        addNew: 'Přidat nového člena',
        editMember: 'Upravit člena',
        deleteMember: 'Smazat člena',
        noMembers: 'Nenalezeni žádní členové',
        selectGroup: 'Vyberte skupinu...',
        fields: {
          number: 'Číslo',
          name: 'Jméno',
          group: 'Skupina',
          createdAt: 'Vytvořeno',
          updatedAt: 'Aktualizováno',
        },
        validation: {
          numberRequired: 'Číslo je povinné',
          numberPositive: 'Číslo musí být kladné',
          nameRequired: 'Jméno je povinné',
          groupRequired: 'Skupina je povinná',
        },
        messages: {
          createSuccess: 'Člen byl úspěšně vytvořen',
          updateSuccess: 'Člen byl úspěšně aktualizován',
          deleteSuccess: 'Člen byl úspěšně smazán',
          deleteConfirm: 'Opravdu chcete smazat tohoto člena?',
          numberExists: 'Číslo člena již existuje',
        },
      },
      games: {
        loading: 'Načítám statistiky...',
        loadingScores: 'Načítám výsledky...',
        loadError: 'Nepodařilo se načíst statistiky',
        totalGames: 'Celkem her',
        activeGames: 'Aktivních',
        totalDeaths: 'Celkem úmrtí',
        totalTime: 'Celkový čas smrti',
        overallLeaderboard: 'Celkové pořadí',
        gamesHistory: 'Historie her',
        newGame: 'Nová hra',
        noGames: 'Zatím žádné hry',
        active: 'Aktivní',
        ongoing: 'Probíhá',
        member: 'Člen',
        deathTime: 'Čas smrti',
        deaths: 'Úmrtí',
        gamesPlayed: 'Her',
        gameName: 'Název hry',
        gameNamePlaceholder: 'např. Sobotní Mrtvoliště',
        nameRequired: 'Název hry je povinný',
        createError: 'Nepodařilo se vytvořit hru',
        started: 'Zahájeno',
        ended: 'Ukončeno',
        duration: 'Trvání',
        results: 'Výsledky',
        noScores: 'Zatím žádné výsledky',
        internalMrtvoliste: 'Interní mrtvoliště',
        endGame: 'Ukončit hru',
        ending: 'Ukončuji...',
        confirmEnd: 'Opravdu chcete ukončit tuto hru?',
        endError: 'Nepodařilo se ukončit hru',
        noHostinaResults: 'Zatím žádné výsledky z Hostiny',
        recipe: 'recept',
        recipes: 'receptů',
        gameHistory: 'Historie her',
        allCompletedGames: 'Všechny dokončené hry',
        viewResults: 'Zobrazit výsledky',
        backToOverview: 'Zpět na přehled',
        overallStatistics: 'Celkové statistiky',
        comprehensiveOverview: 'Komplexní přehled všech her',
        basicOverview: 'Základní přehled',
        completed: 'Dokončeno',
        totalDeathTime: 'Celkový čas úmrtí',
        avgDeathsPerGame: 'Průměr úmrtí/hru',
        gameDuration: 'Délka her',
        avgGameDuration: 'Průměrná délka hry',
        totalGameTime: 'Celkový herní čas',
        mostDeadPlayers: 'Nejvíce mrtví hráči',
        noData: 'Nejsou dostupná žádná data',
        mostValuablePlayers: 'Nejužitečnější hráči',
        totalVotes: 'Celkem hlasů',
        gamesVoted: 'Zahlasovaných her',
      },
      groups: {
        title: 'Skupiny',
        subtitle: 'Spravujte členské skupiny a týmy',
        addNew: 'Přidat novou skupinu',
        create: 'Vytvořit skupinu',
        edit: 'Upravit skupinu',
        members: 'členů',
        empty: {
          title: 'Zatím žádné skupiny',
          description: 'Vytvořte první skupinu pro organizaci členů do týmů.',
          action: 'Vytvořit první skupinu',
        },
        form: {
          name: 'Název skupiny',
          namePlaceholder: 'Zadejte název skupiny...',
        },
        delete: {
          title: 'Smazat skupinu?',
          message: 'Opravdu chcete smazat skupinu "{{name}}"? Tuto akci nelze vrátit zpět.',
          warning: 'Tato skupina má {{count}} členů. Ztratí přiřazení ke skupině.',
        },
        error: {
          loadFailed: 'Nepodařilo se načíst skupiny',
          nameRequired: 'Název skupiny je povinný',
          saveFailed: 'Nepodařilo se uložit skupinu',
          deleteFailed: 'Nepodařilo se smazat skupinu',
        },
      },
      errors: {
        fetchFailed: 'Nepodařilo se načíst členy',
        createFailed: 'Nepodařilo se vytvořit člena',
        updateFailed: 'Nepodařilo se aktualizovat člena',
        deleteFailed: 'Nepodařilo se smazat člena',
        networkError: 'Chyba sítě. Zkontrolujte prosím připojení.',
      },
      clientSelector: {
        label: 'Klient',
        allClients: 'Všichni klienti (Admin pohled)',
        selected: 'Vybrán',
        loading: 'Načítám klienty...',
        loadError: 'Nepodařilo se načíst klienty',
      },
      privacy: {
        title: 'Zásady ochrany osobních údajů',
        lastUpdated: 'Poslední aktualizace',
        dataCollection: {
          title: 'Jaká data sbíráme',
          intro: 'Aplikace Hvozdici sbírá následující data pro poskytování své funkčnosti:',
          deviceId: 'Identifikátor zařízení pro párování',
          qrCodes: 'QR kódy účastníků her',
          gameStats: 'Herní statistiky (skóre, časy)',
          notifications: 'Push notification tokeny',
        },
        dataUse: {
          title: 'Jak používáme vaše data',
          description: 'Shromážděná data používáme výhradně pro poskytování funkčnosti aplikace a herních režimů.',
        },
        dataSharing: {
          title: 'Sdílení dat',
          description: 'Vaše data nesdílíme s třetími stranami.',
        },
        dataRetention: {
          title: 'Uchovávání dat',
          description: 'Data jsou uložena na našem serveru po dobu používání aplikace.',
        },
        security: {
          title: 'Zabezpečení dat',
          description: 'Implementujeme vhodná technická a organizační opatření k ochraně vašich dat.',
        },
        userRights: {
          title: 'Vaše práva',
          intro: 'Máte následující práva ohledně vašich dat:',
          access: 'Právo na přístup k vašim datům',
          deletion: 'Právo požádat o smazání vašich dat',
          correction: 'Právo na opravu nepřesných dat',
        },
        contact: {
          title: 'Kontakt',
          description: 'Pokud máte jakékoli dotazy ohledně ochrany vašich osobních údajů, kontaktujte nás na:',
        },
        footer: 'Tyto zásady ochrany osobních údajů se vztahují na mobilní aplikaci Hvozdici a související webové služby.',
      },
      admin: {
        title: 'Administrace',
        subtitle: 'Spravujte systémová data a provádějte hromadné operace',
        warning: {
          title: 'Varování: Destruktivní operace',
          message: 'Tyto operace nelze vrátit zpět. Před pokračováním se ujistěte, že máte zálohu.',
        },
        deleteStats: {
          title: 'Smazat veškeré statistiky',
          description: 'Odstranit všechny herní záznamy a výsledky z databáze.',
          item1: 'Všechny hry budou smazány',
          item2: 'Všechny záznamy o úmrtích budou smazány',
          item3: 'Celkové statistiky budou resetovány',
          button: 'Smazat veškeré statistiky',
        },
        deleteHistory: {
          title: 'Smazat historii her',
          description: 'Odstranit pouze ukončené hry a jejich výsledky. Aktivní hry zůstanou zachovány.',
          item1: 'Pouze ukončené hry budou smazány',
          item2: 'Aktivní hry zůstanou zachovány',
          item3: 'Celkové statistiky budou aktualizovány',
          button: 'Smazat historii her',
        },
        deleteGroups: {
          title: 'Smazat všechny skupiny',
          description: 'Odstranit všechny skupiny z databáze.',
          item1: 'Všechny skupiny budou smazány',
          item2: 'Členové ztratí přiřazení ke skupinám',
          button: 'Smazat všechny skupiny',
        },
        deleteMembers: {
          title: 'Smazat všechny členy',
          description: 'Odstranit všechny členy z databáze.',
          item1: 'Všichni členové budou smazáni',
          item2: 'Všechny související statistiky zůstanou osiřelé',
          button: 'Smazat všechny členy',
        },
        confirm: {
          statsTitle: 'Smazat veškeré statistiky?',
          statsMessage: 'Tímto trvale smažete všechny hry a záznamy o úmrtích. Tuto akci nelze vrátit zpět. Opravdu chcete pokračovat?',
          historyTitle: 'Smazat historii her?',
          historyMessage: 'Tímto trvale smažete všechny ukončené hry a jejich záznamy o úmrtích. Aktivní hry zůstanou zachovány. Tuto akci nelze vrátit zpět. Opravdu chcete pokračovat?',
          groupsTitle: 'Smazat všechny skupiny?',
          groupsMessage: 'Tímto trvale smažete všechny skupiny. Členové ztratí přiřazení ke skupinám. Tuto akci nelze vrátit zpět. Opravdu chcete pokračovat?',
          membersTitle: 'Smazat všechny členy?',
          membersMessage: 'Tímto trvale smažete všechny členy z databáze. Tuto akci nelze vrátit zpět. Opravdu chcete pokračovat?',
        },
        messages: {
          statsDeleted: 'Všechny statistiky byly úspěšně smazány.',
          statsDeleteError: 'Nepodařilo se smazat statistiky. Zkuste to prosím znovu.',
          historyDeleted: 'Historie her byla úspěšně smazána.',
          historyDeleteError: 'Nepodařilo se smazat historii her. Zkuste to prosím znovu.',
          groupsDeleted: 'Všechny skupiny byly úspěšně smazány.',
          groupsDeleteError: 'Nepodařilo se smazat skupiny. Zkuste to prosím znovu.',
          membersDeleted: 'Všichni členové byli úspěšně smazáni.',
          membersDeleteError: 'Nepodařilo se smazat členy. Zkuste to prosím znovu.',
        },
        devices: {
          title: 'Správa zařízení',
          description: 'Spravujte spárovaná mobilní zařízení pro tohoto klienta',
          pairButton: 'Spárovat zařízení',
          pairFirstDevice: 'Spárovat první zařízení',
          activeTab: 'Aktivní',
          unpairedTab: 'Odpárované',
          noClient: 'Není vybrán klient',
          selectClient: 'Prosím vyberte klienta z rozbalovací nabídky výše pro správu zařízení.',
          noClientSelected: 'Není vybrán žádný klient',
          noDevices: 'Žádná spárovaná zařízení',
          noDevicesDescription: 'Tento klient nemá zatím žádná spárovaná mobilní zařízení.',
          noUnpairedDevices: 'Žádná odpárovaná zařízení',
          noUnpairedDescription: 'Žádná zařízení nebyla odpárována.',
          loadError: 'Nepodařilo se načíst zařízení',
          paired: 'Spárováno',
          unpaired: 'Odpárováno',
          platform: 'Platforma',
          appVersion: 'Verze aplikace',
          confirmUnpair: 'Opravdu chcete odpárovat toto zařízení? Mobilní aplikace bude odhlášena.',
          unpairButton: 'Odpárovat zařízení',
          unpairing: 'Odpárování...',
          unpairError: 'Nepodařilo se odpárovat zařízení',
          admin: 'Admin',
          grantAdmin: 'Udělit admin práva',
          removeAdmin: 'Odebrat admin práva',
          adminToggleError: 'Nepodařilo se změnit admin práva',
        },
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Přepsané obrazovky mají texty zatím jen česky, takže detekci jazyka nepouštíme
    // ke slovu — jinak by anglická navigace stála nad českým obsahem. Až se řetězce
    // z obrazovek vytáhnou do resources, stačí lng zase odebrat.
    lng: 'cs',
    fallbackLng: 'cs',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
